import { IncomeStatementChart } from 'features/Financials/components/IncomeStatementChart'
import { cn } from 'lib/utils'

import { EmptyBlock } from '../EmptyBlock'
import { KpisBlock } from '../generic/GenericBlocks'
import { asArray, asNumber, asText } from '../payload'

/**
 * Renderers for the financial elements (MYD-503). Payload shapes mirror
 * backend/presentations/providers/financialElements.js, where every figure
 * is already formatted.
 *
 * A viewer without financial access never reaches these: the server resolves
 * their blocks as `restricted` and SlideRenderer shows the neutral
 * placeholder. So these only ever draw figures the viewer may see, and say
 * plainly when there are none, rather than drawing zeros.
 */

/** Figure tiles, as the Key figures element draws them. */
export function FinancialFiguresBlock({ payload }: { payload: any }) {
	const empty = asText(payload?.empty)
	if (empty) return <EmptyBlock>{empty}</EmptyBlock>
	return <KpisBlock payload={{ items: asArray(payload?.items) }} />
}

/** The Financials tab's own chart, filling the block. */
export function IncomeChartBlock({ payload }: { payload: any }) {
	const empty = asText(payload?.empty)
	const statement = payload?.incomeStatement
	if (empty || !statement || !Array.isArray(statement.quarters) || !Array.isArray(statement.lineItems)) {
		return <EmptyBlock fill>{empty || 'No income statement to chart.'}</EmptyBlock>
	}
	// Only the fields the chart reads; the rest of its type is the grid's.
	const incomeStatement = {
		quarters: statement.quarters,
		lineItems: statement.lineItems.map((item: any) => ({ ...item, values: asArray(item?.values) })),
	}
	return (
		<div className="h-full min-h-[240px]">
			<IncomeStatementChart incomeStatement={incomeStatement as any} className="h-full" oldestFirst />
		</div>
	)
}

/** The Portfolio Financials tab's figures: totals as tiles, or by location as a table. */
export function PortfolioFinancialsBlock({ payload }: { payload: any }) {
	const empty = asText(payload?.empty)
	if (empty) return <EmptyBlock>{empty}</EmptyBlock>
	if (payload?.view !== 'byLocation') return <KpisBlock payload={{ items: asArray(payload?.items) }} />

	const columns = asArray<string>(payload?.columns)
	const rows = asArray<any>(payload?.rows)
	const total = payload?.total
	const more = asNumber(payload?.more) ?? 0

	return (
		<div>
			<table className="w-full table-fixed text-left">
				<thead>
					<tr className="border-b-2 border-gray-200 text-[16px] uppercase tracking-wide text-gray-500">
						<th className="py-2 pr-4 font-semibold">Location</th>
						{columns.map((column, index) => (
							<th key={index} className="py-2 pl-4 text-right font-semibold">
								{asText(column)}
							</th>
						))}
					</tr>
				</thead>
				<tbody>
					{rows.map((row, index) => (
						<tr key={index} className="border-b border-gray-100 text-[20px]">
							<td className="truncate py-2.5 pr-4 font-medium text-gray-900">{asText(row?.name)}</td>
							{asArray<string>(row?.values).map((value, cell) => (
								<td key={cell} className="py-2.5 pl-4 text-right tabular-nums text-gray-700">
									{asText(value)}
								</td>
							))}
						</tr>
					))}
				</tbody>
				{total && (
					<tfoot>
						<tr className="text-[20px] font-semibold text-gray-900">
							<td className="pt-3 pr-4">{asText(total.name)}</td>
							{asArray<string>(total.values).map((value, cell) => (
								<td key={cell} className="pt-3 pl-4 text-right tabular-nums">
									{asText(value)}
								</td>
							))}
						</tr>
					</tfoot>
				)}
			</table>
			<p className="mt-3 text-[16px] text-gray-500">
				{asText(payload?.caption)}
				{more > 0 && ` · and ${more} more ${more === 1 ? 'location' : 'locations'} in the total`}
			</p>
		</div>
	)
}

/** The evaluation comparison view's financial rows, best in each row marked as there. */
export function SiteFinancialsBlock({ payload }: { payload: any }) {
	const empty = asText(payload?.empty)
	if (empty) return <EmptyBlock>{empty}</EmptyBlock>
	const sites = asArray<any>(payload?.sites)
	const rows = asArray<any>(payload?.rows)

	return (
		<table className="w-full table-fixed text-left">
			<thead>
				<tr className="border-b-2 border-gray-200 text-[16px] text-gray-500">
					<th className="py-2 pr-4 font-semibold uppercase tracking-wide">Metric</th>
					{sites.map((site, index) => (
						<th key={index} className="py-2 pl-4 text-right font-semibold">
							<span className="mr-1.5 inline-flex h-6 w-6 items-center justify-center rounded-full bg-gray-800 text-[13px] text-white">{asText(site?.letter)}</span>
							<span className="text-gray-800">{asText(site?.name)}</span>
						</th>
					))}
				</tr>
			</thead>
			<tbody>
				{rows.map((row, index) => {
					const best = asArray<boolean>(row?.best)
					return (
						<tr key={index} className="border-b border-gray-100 text-[20px]">
							<td className="py-2.5 pr-4 text-gray-500">{asText(row?.label)}</td>
							{asArray<string>(row?.values).map((value, cell) => (
								<td key={cell} className={cn('py-2.5 pl-4 text-right tabular-nums', best[cell] ? 'font-semibold text-success' : 'text-gray-800')}>
									{asText(value)}
									{best[cell] && <span className="ml-1.5 text-[14px]" aria-label="best in row">★</span>}
								</td>
							))}
						</tr>
					)
				})}
			</tbody>
		</table>
	)
}
